import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import HeaderPlatform from "../components/HeaderPlatform";
import LeftSidebar from "../components/LeftSidebar";
import { ExerciseBlock } from "../components/ExerciseBlock";
import FooterPlatform from "../components/FooterPlatform";
import GlassCard from "../../components/GlassCard";
import { getSessionById, completeSessionExercise } from "../api/planApi";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";
import NotFoundPage from "./NotFoundPage";

export default function SessionPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date");

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    async function loadSession() {
      try {
        setLoading(true);
        setError("");
        const data = await getSessionById(sessionId);
        setSession(data);
      } catch (err) {
        setError(err.message || "Failed to load session");
      } finally {
        setLoading(false);
      }
    }

    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  const exercises = useMemo(() => {
    const parseCsvToArray = (value) => {
      if (!value) return [];
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    };

    return (session?.exercises || []).map((item) => {
      const exercise = item.exercise;

      const isBreak =
        !exercise &&
        !item.exercise_id &&
        String(item.notes || "").toLowerCase() === "break";

      return {
        id: item.id,
        name: isBreak ? "Break" : exercise?.name ?? "Unnamed exercise",
        difficulty: isBreak ? "-" : exercise?.difficulty ?? "-",
        sets: item.sets ?? 0,
        estimatedTimeSec: item.total_time_seconds ?? item.duration_seconds ?? 0,
        inSession: true,
        completed: session?.status === "completed",
        isBreak,

        sessionImages:
          !isBreak && exercise?.detail_images_url
            ? [exercise.detail_images_url]
            : [],
        sessionYoutubeUrl: !isBreak ? exercise?.detail_youtube_url ?? "" : "",
        shortDescription: isBreak
          ? item.notes ?? "Break"
          : exercise?.long_description ?? "",
        sessionMusclesImageUrl: !isBreak
          ? exercise?.detail_muscles_images_url ?? ""
          : "",

        timerConfig: {
          setCount: item.sets ?? 0,
          workSecPerSet: item.duration_seconds ?? 0,
          restSecBetweenSets: item.break_seconds ?? 0,
        },

        muscles: isBreak
          ? []
          : [
              ...parseCsvToArray(exercise?.primary_muscles),
              ...parseCsvToArray(exercise?.secondary_muscles),
            ],
        longDescription: isBreak
          ? item.notes ?? "Break"
          : exercise?.long_description ?? "",
        detailImages:
          !isBreak && exercise?.detail_images_url
            ? [exercise.detail_images_url]
            : [],
        detailMusclesImageUrl: !isBreak
          ? exercise?.detail_muscles_images_url ?? ""
          : "",
        detailYoutubeUrl: !isBreak ? exercise?.detail_youtube_url ?? "" : "",
        commonMistakes: isBreak
          ? []
          : parseCsvToArray(exercise?.common_mistakes),

        reps: item.reps ?? 0,
        breakSeconds: item.break_seconds ?? 0,
        totalTimeSeconds: item.total_time_seconds ?? 0,
        intensity: item.intensity ?? "",
        notes: item.notes ?? "",
        exerciseId: item.exercise_id ?? null,
        sortOrder: item.sort_order ?? 0,
        primaryMuscles: exercise?.primary_muscles ?? "",
        secondaryMuscles: exercise?.secondary_muscles ?? "",
        body_region: exercise?.body_region ?? [],
        itemStatus: item.status ?? "planned",
      };
    });
  }, [session]);

  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCompleteExercise = async (id) => {
    try {
      await completeSessionExercise(id, true);

      setSession((prev) => {
        if (!prev) return prev;

        const updatedExercises = (prev.exercises || []).map((item) =>
          item.id === id
            ? {
                ...item,
                status: "completed",
                completed: 1,
              }
            : item
        );

        const realExercises = updatedExercises.filter(
          (item) => item.exercise_id !== null
        );

        const allCompleted =
          realExercises.length > 0 &&
          realExercises.every(
            (item) =>
              item.status === "completed" ||
              Boolean(item.completed)
          );

        return {
          ...prev,
          exercises: updatedExercises,
          status: allCompleted ? "completed" : prev.status,
        };
      });

      setExpandedId(null);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save exercise progress");
    }
  };

  const sessionStatusLabel = useMemo(() => {
    if (!session?.status) return "Planned";

    switch (session.status) {
      case "completed":
        return "Completed";
      case "skipped":
        return "Skipped";
      case "planned":
      default:
        return "Planned";
    }
  }, [session]);

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage err={error} />;
  if (!session) return <NotFoundPage />;

  return (
    <div className="revimo-layout">
      <HeaderPlatform />
      <main className="revimo-main">
        <LeftSidebar />

        <div className="revimo-page-content">
          <GlassCard style={{ padding: "10px", maxWidth: "775px", marginLeft: "50px" }}>
            <div className="profile-section header-section">
              <div className="profile-col profile-col-left">
                <div
                  className="profile-main-name"
                  style={{ paddingTop: "10px", color: "#0b1720" }}
                >
                  {session.name}
                </div>

                <div style={{ textAlign: "start" }}>
                  <div className="profile-main-username" style={{ color: "#4c6a7a" }}>
                    session-{session.id}
                  </div>

                  <div className="profile-main-sex" style={{ color: "#4c6a7a" }}>
                    Estimated time: {session.estimated_time ?? "-"}
                  </div>

                  <div className="profile-main-sex" style={{ color: "#4c6a7a" }}>
                    Status: {sessionStatusLabel}
                  </div>

                  {date && (
                    <div className="profile-main-sex" style={{ color: "#4c6a7a" }}>
                      Date: {date}
                    </div>
                  )}
                </div>

                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  {session.description || "No description yet."}
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard
            className="exercise-header"
            style={{
              padding: "10px",
              maxWidth: "775px",
              marginLeft: "50px",
              height: "40px",
              background: "#eef5ff89",
              marginTop: "25px",
            }}
          >
            <div className="header-cell name">Exercise Name:</div>
            <div className="header-cell name">Difficulty:</div>
            <div className="header-cell name">Number of sets:</div>
            <div className="header-cell name">Estimated Time:</div>
            <div className="header-cell name"></div>
          </GlassCard>

          <div
            className="flex flex-col gap-3"
            style={{ maxWidth: "850px", marginLeft: "57px" }}
          >
            <GlassCard style={{ padding: "10px" }}>
              {exercises.map((ex) => (
                <ExerciseBlock
                  key={ex.id}
                  {...ex}
                  isExpanded={expandedId === ex.id}
                  option="inSession"
                  onToggleExpand={handleToggleExpand}
                  onCompleteExercise={handleCompleteExercise}
                  l_m="5px"
                />
              ))}
            </GlassCard>
          </div>
        </div>
      </main>

      <FooterPlatform />
    </div>
  );
}